import { create } from "zustand"
import type { AuthUser } from "@/lib/firebase/auth"

/**
 * Who is signed in, kept apart from the session store on purpose.
 *
 * Sign-in outlives any one session and exists on screens that have no session at
 * all — the customer list is the whole workspace. Folding it into the session
 * store would mean `clearSessionData` had to remember not to touch it, which is
 * exactly the kind of rule that gets forgotten once.
 *
 *   checking   the observer has not answered yet. The app must WAIT here rather
 *              than render the sign-in screen: Firebase restores a persisted
 *              session asynchronously, so a signed-in facilitator would see the
 *              sign-in button flash on every reload.
 *   signedIn   a facilitator on the allowed domain.
 *   signedOut  nobody, or an account that was refused.
 */
export type AuthStatus = "checking" | "signedIn" | "signedOut"

export interface AuthState {
  user: AuthUser | null
  status: AuthStatus
  /**
   * Why the last attempt failed — a wrong domain, a closed popup, a network
   * problem. Kept out of the session store's `error`, which is the write channel
   * for a live session and surfaces as a toast over the room's screen.
   */
  error: string | null
  /** True while the popup is open, so the button can say so and refuse a second. */
  pending: boolean

  applyUser: (user: AuthUser | null) => void
  setError: (message: string | null) => void
  setPending: (pending: boolean) => void
}

export const useAuthStore = create<AuthState>()((set) => ({
  user: null,
  status: "checking",
  error: null,
  pending: false,

  // One update: separate setters would briefly expose an arrived user while the
  // status still read "checking", and every route guard reads both.
  applyUser: (user) => set({ user, status: user ? "signedIn" : "signedOut", pending: false }),
  setError: (error) => set({ error }),
  setPending: (pending) => set({ pending }),
}))

export const selectAuthUser = (s: AuthState): AuthUser | null => s.user
export const selectAuthUid = (s: AuthState): string | null => s.user?.uid ?? null
export const selectIsSignedIn = (s: AuthState): boolean => s.status === "signedIn"
/** True until Firebase has told us whether a persisted session exists. */
export const selectAuthChecking = (s: AuthState): boolean => s.status === "checking"
export const selectAuthError = (s: AuthState): string | null => s.error
export const selectAuthPending = (s: AuthState): boolean => s.pending
